import { z } from "zod";
function hasControl(value) {
    return [...value].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127);
}
const label = (max) => z
    .string()
    .trim()
    .min(2)
    .max(max)
    .refine((value) => !hasControl(value), "Control characters are not permitted");
/** Registration is a private product-reference fetch policy, never fiscal authority. */
export const privateProductSourceHostnameSchema = z
    .string()
    .min(3)
    .max(253)
    .regex(/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/)
    .refine((host) => !/(?:^|\.)(?:gov|leg|jus|mil)(?:\.|$)/.test(host) &&
    !/(?:^|\.)(?:localhost|local|localdomain|internal|intranet|lan|home|corp|test|invalid|example|onion|arpa|alt)$/.test(host) &&
    !host.split(".").some((label) => label.startsWith("xn--")), "Must be an exact public private-company DNS hostname, without government or special-use names");
const path = z
    .string()
    .min(2)
    .max(1000)
    .regex(/^\/[\x21-\x7e]+$/)
    .refine((value) => !/[\\%?#|]/.test(value) &&
    !value.includes("//") &&
    !value.endsWith("/") &&
    !value.split("/").some((segment) => segment === "." || segment === ".."), "Must be a non-root path prefix without traversal, encoding, query or fragment");
function validEntry(raw) {
    if (!raw.startsWith("https://") || /[\s\\#]/u.test(raw) || hasControl(raw))
        return false;
    // URL.port erases an explicit :443, so reject all explicitly supplied ports before parsing.
    const authority = raw.match(/^https:\/\/([^/?#]+)/)?.[1];
    if (!authority || /[:@]/.test(authority))
        return false;
    try {
        const url = new URL(raw);
        if (!privateProductSourceHostnameSchema.safeParse(url.hostname).success ||
            url.hostname !== authority ||
            url.hash ||
            url.username ||
            url.password)
            return false;
        const rawPath = raw.slice(`https://${authority}`.length).split(/[?#]/)[0] ?? "";
        return !rawPath.split("/").some((segment) => {
            try {
                const decoded = decodeURIComponent(segment);
                return (decoded === "." || decoded === ".." || /[/%\\]/u.test(decoded) || hasControl(decoded));
            }
            catch {
                return true;
            }
        });
    }
    catch {
        return false;
    }
}
const privateEntryUrl = z.string().max(2048).refine(validEntry, "Must be a safe public HTTPS URL");
export const privateProductSourceCreateSchema = z
    .object({
    hostname: privateProductSourceHostnameSchema,
    name: label(100),
    publisher: label(160),
    entryUrls: z.array(privateEntryUrl).min(1).max(10),
    allowedPathPrefixes: z.array(path).max(20).default([]),
    sourceReference: label(2000).refine((value) => value.length >= 10, "A source reference of at least ten characters is required"),
})
    .strict()
    .superRefine((value, context) => {
    for (const [index, entry] of value.entryUrls.entries()) {
        if (!validEntry(entry))
            continue;
        if (new URL(entry).hostname !== value.hostname)
            context.addIssue({
                code: "custom",
                path: ["entryUrls", index],
                message: "Every entry URL must use this exact hostname",
            });
    }
    if (new Set(value.entryUrls.map((url) => (validEntry(url) ? new URL(url).toString() : url)))
        .size !== value.entryUrls.length)
        context.addIssue({ code: "custom", path: ["entryUrls"], message: "URLs must be unique" });
    if (new Set(value.allowedPathPrefixes).size !== value.allowedPathPrefixes.length)
        context.addIssue({
            code: "custom",
            path: ["allowedPathPrefixes"],
            message: "Prefixes must be unique",
        });
});
/** Individually admit additional exact documents without widening any path or host policy. */
export const privateProductSourceEntryUrlsAppendSchema = z
    .object({
    expectedRegistrationSha256: z.string().regex(/^[a-f0-9]{64}$/),
    entryUrls: z.array(privateEntryUrl).min(1).max(8),
    sourceReference: label(2000).refine((value) => value.length >= 10, "A source reference of at least ten characters is required"),
})
    .strict()
    .superRefine((value, context) => {
    if (new Set(value.entryUrls.map((url) => (validEntry(url) ? new URL(url).toString() : url)))
        .size !== value.entryUrls.length)
        context.addIssue({ code: "custom", path: ["entryUrls"], message: "URLs must be unique" });
});
export const privateProductSourceRegistrationSchema = z
    .object({
    sourceId: z.string().uuid(),
    sourceKey: z.string().regex(/^product-[a-f0-9]{50}$/),
    supplierId: z.string().uuid(),
    hostname: privateProductSourceHostnameSchema,
    name: z.string().min(2).max(160),
    publisher: z.string().min(2).max(160),
    entryUrls: z.array(privateEntryUrl).min(1).max(10),
    allowedHosts: z.tuple([privateProductSourceHostnameSchema]),
    allowedPathPrefixes: z.array(path).max(20),
    status: z.enum(["ACTIVE", "DEGRADED", "PAUSED", "DISABLED"]),
    purpose: z.literal("PRIVATE_PRODUCT_REFERENCE"),
    authority: z.literal("NONE"),
    identityStatus: z.literal("NOT_ASSERTED"),
    registrationSha256: z.string().regex(/^[a-f0-9]{64}$/),
    createdAt: z.string().datetime(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.allowedHosts[0] !== value.hostname ||
        value.entryUrls.some((entry) => validEntry(entry) && new URL(entry).hostname !== value.hostname))
        context.addIssue({
            code: "custom",
            path: ["hostname"],
            message: "Source hosts must match exactly",
        });
});
//# sourceMappingURL=private-product-sources.js.map